import { useState, useEffect, useMemo } from "react";

export async function fetchNetworkStats(coin) {
  try {
    console.log(`[fetchNetworkStats] Запрос данных для монеты: ${coin}`);

    const res = await fetch(`/api/coins/${coin}`, {
      headers: {
        Accept: "application/json",
        "User-Agent": "MiningCalculator/1.0",
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status} ${res.statusText}`);
    }

    const json = await res.json();

    // Если сервер вернул ошибку в теле
    if (json.error) {
      throw new Error(`Server error: ${json.error}`);
    }

    const result = {
      difficulty: Number(json?.difficulty ?? 0),
      blockReward: Number(json?.blockReward ?? 0),
      price: Number(json?.price ?? 0),
      hashrate: json?.hashrate || null,
      algorithm: json?.algorithm || null,
      poolFeePercent: Number(json?.poolFeePercent ?? 1.5),
    };

    console.log(`[fetchNetworkStats] Данные для ${coin}:`, result);
    return result;
  } catch (err) {
    console.error(`[fetchNetworkStats] Ошибка получения данных сети для ${coin}:`, err);

    // Fallback значения
    return {
      difficulty: 0,
      blockReward: 0,
      price: 0,
      hashrate: null,
      algorithm: null,
      poolFeePercent: 2,
    };
  }
}

export async function fetchUsdRub() {
  try {
    const res = await fetch("https://www.cbr-xml-daily.ru/daily_json.js");
    if (!res.ok) throw new Error("CBR fetch error");
    const json = await res.json();
    return Number(json?.Valute?.USD?.Value ?? 90);
  } catch (err) {
    console.error("Ошибка получения курса USD→RUB:", err);
    return 90;
  }
}

export default function useMiningCalculator(props = {}) {
  const {
    currency = "USD",
    initialHashrate = 100,
    hashrateUnit = "TH/s",
    initialPowerW = 3200,
    initialElectricityUsdPerKWh = 0.12,
    initialElectricityRubPerKWh = 5.25,
    initialFarmCostUSD = 1500,
    coin = "BTC",
    algorithm = "SHA-256",
  } = props;

  const hashrate = props.hashrate ?? initialHashrate;

  // Состояния для данных сети
  const [difficulty, setDifficulty] = useState(null);
  const [blockReward, setBlockReward] = useState(null);
  const [coinPriceUsd, setCoinPriceUsd] = useState(null);
  const [networkHashrate, setNetworkHashrate] = useState(null);
  const [networkAlgorithm, setNetworkAlgorithm] = useState(null);
  
  // Состояния для валют и затрат
  const [usdRub, setUsdRub] = useState(null);
  const [powerW, setPowerW] = useState(initialPowerW);
  const [electricityUsdPerKWh, setElectricityUsdPerKWh] = useState(initialElectricityUsdPerKWh);
  const [electricityRubPerKWh, setElectricityRubPerKWh] = useState(initialElectricityRubPerKWh);
  const [farmCostUSD, setFarmCostUSD] = useState(initialFarmCostUSD);
  const [poolFeePercent, setPoolFeePercent] = useState(null);

  // --- USD→RUB
  useEffect(() => {
    let canceled = false;
    (async () => {
      const rate = await fetchUsdRub();
      if (!canceled) setUsdRub(rate);
      console.log("[useMiningCalculator] USD→RUB rate:", rate);
    })();
    return () => {
      canceled = true;
    };
  }, []);

  // --- Данные монеты с нашего бэкенда
  useEffect(() => {
    let canceled = false;
    (async () => {
      const stats = await fetchNetworkStats(coin);
      if (!canceled) {
        setDifficulty(stats.difficulty);
        setBlockReward(stats.blockReward);
        setCoinPriceUsd(stats.price);
        setNetworkHashrate(stats.hashrate);
        setNetworkAlgorithm(stats.algorithm);
        setPoolFeePercent(stats.poolFeePercent);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [coin]);

  // --- BTC/USD для обратной совместимости (если вдруг нужен)
  const btcUsd = useMemo(() => {
    if (coin === "BTC") return coinPriceUsd;
    return null;
  }, [coin, coinPriceUsd]);

  // --- Множители единиц хешрейта
  const unitMultiplier = {
    "EH/s": 1e18,
    "PH/s": 1e15,
    "TH/s": 1e12,
    "GH/s": 1e9,
    "MH/s": 1e6,
    "KH/s": 1e3,
    "H/s": 1,
  };

  // --- Дефолтные единицы для алгоритмов
  const algorithmUnits = {
    "SHA-256": "TH/s",
    "Scrypt": "GH/s",
    "Etchash": "GH/s",
    "Equihash": "KH/s",
    "kHeavyHash": "TH/s",
    "Eaglesong": "TH/s",
    "zkSNARK": "MH/s",
    "Blake3": "TH/s",
    "x11": "GH/s",
    "Handshake": "TH/s",
    "Blake2S": "TH/s",
  };

  // --- Результаты расчёта
  const results = useMemo(() => {
    // Проверяем, что все необходимые данные загружены
    if (
      difficulty == null ||
      blockReward == null ||
      coinPriceUsd == null ||
      usdRub == null ||
      poolFeePercent == null
    ) {
      return null;
    }

    // Если difficulty или blockReward равны 0 - данные ещё не загружены или ошибка
    if (difficulty === 0 || blockReward === 0 || coinPriceUsd === 0) {
      return null;
    }

    const secondsPerDay = 86400;
    const avgDaysPerMonth = 30.4375;
    const secondsPerMonth = secondsPerDay * avgDaysPerMonth;

    // Конвертируем хешрейт пользователя в H/s
    const unit = hashrateUnit || algorithmUnits[algorithm] || "H/s";
    const multiplier = unitMultiplier[unit] || 1;
    const hashRateHperSec = hashrate * multiplier;

    // Комиссия пула
    const poolFactor = 1 - poolFeePercent / 100;

    // Расчёт добычи монет
    // Формула: (Hashrate * BlockReward * Time) / (Difficulty * 2^32)
    const coinPerSecGross = (hashRateHperSec * blockReward) / (difficulty * 4294967296);
    const coinPerSecNet = coinPerSecGross * poolFactor;

    const coinPerDay = coinPerSecNet * secondsPerDay;
    const coinPerWeek = coinPerDay * 7;
    const coinPerMonth = coinPerSecNet * secondsPerMonth;
    const coinPerYear = coinPerMonth * 12;

    // Конвертируем цену монеты в выбранную валюту
    const coinPriceCurrency = currency === "USD" 
      ? coinPriceUsd 
      : coinPriceUsd * usdRub;

    // Доход в валюте
    const revenueDay = coinPerDay * coinPriceCurrency;
    const revenueWeek = coinPerWeek * coinPriceCurrency;
    const revenueMonth = coinPerMonth * coinPriceCurrency;
    const revenueYear = coinPerYear * coinPriceCurrency;

    // Расходы на электричество
    const kWhPerDay = (powerW / 1000) * 24;
    const costPerKWhCurrency = currency === "USD"
      ? electricityUsdPerKWh
      : (electricityRubPerKWh ?? electricityUsdPerKWh * usdRub);

    const electricityCostPerDay = kWhPerDay * costPerKWhCurrency;
    const electricityCostPerWeek = electricityCostPerDay * 7;
    const electricityCostPerMonth = electricityCostPerDay * avgDaysPerMonth;
    const electricityCostPerYear = electricityCostPerDay * 365;

    // Стоимость фермы в выбранной валюте
    const farmCostCurrency = currency === "USD" 
      ? farmCostUSD 
      : farmCostUSD * usdRub;

    // Прибыль
    const profitDay = revenueDay - electricityCostPerDay;
    const profitWeek = revenueWeek - electricityCostPerWeek;
    const profitMonth = revenueMonth - electricityCostPerMonth;
    const profitYear = revenueYear - electricityCostPerYear;

    // Окупаемость
    const paybackDays = profitDay > 0 ? farmCostCurrency / profitDay : Infinity;
    const paybackWeeks = profitWeek > 0 ? farmCostCurrency / profitWeek : Infinity;
    const paybackMonths = profitMonth > 0 ? farmCostCurrency / profitMonth : Infinity;

    return {
      coinPerDay,
      coinPerWeek,
      coinPerMonth,
      coinPerYear,
      revenueDay,
      revenueWeek,
      revenueMonth,
      revenueYear,
      electricityCostPerDay,
      electricityCostPerWeek,
      electricityCostPerMonth,
      electricityCostPerYear,
      profitDay,
      profitWeek,
      profitMonth,
      profitYear,
      paybackDays,
      paybackWeeks,
      paybackMonths,
    };
  }, [
    currency,
    hashrate,
    difficulty,
    blockReward,
    coinPriceUsd,
    poolFeePercent,
    hashrateUnit,
    usdRub,
    powerW,
    electricityUsdPerKWh,
    electricityRubPerKWh,
    farmCostUSD,
    algorithm,
  ]);

  return {
    // Входные параметры
    hashrate,
    difficulty,
    blockReward,
    coinPriceUsd,        // ✅ Цена монеты из нашего API
    networkHashrate,     // ✅ Хешрейт сети
    networkAlgorithm,    // ✅ Алгоритм из API
    btcUsd,             // для обратной совместимости
    usdRub,
    powerW,
    electricityUsdPerKWh,
    electricityRubPerKWh,
    farmCostUSD,
    poolFeePercent,
    
    // Сеттеры
    setDifficulty,
    setBlockReward,
    setCoinPriceUsd,
    setPoolFeePercent,
    setUsdRub,
    setPowerW,
    setElectricityUsdPerKWh,
    setElectricityRubPerKWh,
    setFarmCostUSD,
    
    // Результаты
    results,
  };
}